import React, {useId, useRef} from 'react';
import useDialogA11y from '../hooks/useDialogA11y';
import {useOverlayClose} from '../hooks/useOverlayClose';
import IconButton from './IconButton';
import {XIcon} from './icons';

export interface DialogProps {
    /** Закрытый диалог не рендерится вовсе — обработчики клавиатуры не навешиваются. */
    open: boolean;
    onClose: () => void;
    /**
     * Заголовок окна: рисуется в шапке и связывается с диалогом через `aria-labelledby`.
     * Без него шапка не рисуется вовсе — тогда имя окна обязан задать `ariaLabel`.
     */
    title?: React.ReactNode;
    /**
     * Текстовое имя окна, если `title` — не строка (иконка + разметка). Тогда уходит
     * в `aria-label`, а `aria-labelledby` не используется.
     */
    ariaLabel?: string;
    /** Иконка слева от заголовка. */
    icon?: React.ReactNode;
    /** Ряд кнопок решения под контентом. */
    footer?: React.ReactNode;
    /**
     * Идёт необратимая операция: Escape, клик по фону и крестик не закрывают окно,
     * иначе пользователь теряет результат на полпути.
     */
    busy?: boolean;
    /** Крестик в шапке. Выключается для окон, которые закрываются только решением. */
    showClose?: boolean;
    /**
     * Можно ли закрыть окно «мимо» решения — Escape, клик по фону, крестик. `false`
     * для окон, где уход без решения теряет данные (одноразовые recovery-коды).
     */
    dismissible?: boolean;
    /** Классы затемнения. По умолчанию — стандартный скрим со входной анимацией. */
    overlayClassName?: string;
    /** Классы панели. По умолчанию — карточка `max-w-md`. */
    panelClassName?: string;
    /** Классы области контента. */
    bodyClassName?: string;
    /** Классы шапки. */
    headerClassName?: string;
    /** Классы блока «иконка + заголовок» внутри шапки: строкой или столбцом. */
    headerInnerClassName?: string;
    /** Классы ряда кнопок решения. */
    footerClassName?: string;
    'data-testid'?: string;
    children?: React.ReactNode;
}

const DEFAULT_OVERLAY = 'fw-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const DEFAULT_PANEL = 'bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full flex flex-col outline-none';
const DEFAULT_HEADER = 'flex items-center justify-between gap-3 p-4 border-b border-gray-200 dark:border-gray-600';
const DEFAULT_FOOTER = 'flex flex-wrap items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-600';

/**
 * Общее модальное окно приложения (styleguide §Modals, QA-16/U13).
 *
 * Собирает весь контракт диалога в одном месте: `role="dialog"` + `aria-modal`,
 * связь с заголовком, Escape, ловушка фокуса и возврат фокуса на вызвавший элемент
 * (`useDialogA11y`), закрытие по настоящему клику вне панели (`useOverlayClose`)
 * и кнопка закрытия с доступным именем и тач-зоной 44×44.
 *
 * Новые диалоги пишем только через него — раньше каждое окно решало эти вопросы
 * само и половина их не решала вовсе.
 */
const Dialog: React.FC<DialogProps> = ({
    open,
    onClose,
    title,
    ariaLabel,
    icon,
    footer,
    busy = false,
    showClose = true,
    dismissible = true,
    overlayClassName = DEFAULT_OVERLAY,
    panelClassName = DEFAULT_PANEL,
    bodyClassName = 'p-4',
    headerClassName = DEFAULT_HEADER,
    headerInnerClassName = 'flex items-center gap-2 min-w-0',
    footerClassName = DEFAULT_FOOTER,
    'data-testid': testId,
    children,
}) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const closable = !busy && dismissible;
    const requestClose = () => {
        if (closable) {
            onClose();
        }
    };
    const overlayProps = useOverlayClose(requestClose, closable);
    useDialogA11y(panelRef, requestClose, {active: open});

    if (!open) {
        return null;
    }

    const labelled = ariaLabel || !title
        ? {'aria-label': ariaLabel}
        : {'aria-labelledby': titleId};

    return (
        <div
            className={overlayClassName}
            data-testid={testId ? `${testId}-overlay` : undefined}
            {...overlayProps}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                tabIndex={-1}
                data-testid={testId}
                className={panelClassName}
                {...labelled}
            >
                {title && (
                    <div className={headerClassName}>
                        <div className={headerInnerClassName}>
                            {icon}
                            <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                {title}
                            </h2>
                        </div>
                        {showClose && (
                            <IconButton
                                label="Закрыть"
                                onClick={requestClose}
                                disabled={!closable}
                                data-testid={testId ? `${testId}-close` : undefined}
                                className="shrink-0 rounded-lg text-gray-400 hover:text-gray-600
                                           dark:hover:text-gray-300 disabled:opacity-50"
                            >
                                <XIcon className="w-5 h-5"/>
                            </IconButton>
                        )}
                    </div>
                )}

                <div className={bodyClassName}>{children}</div>

                {footer && <div className={footerClassName}>{footer}</div>}
            </div>
        </div>
    );
};

export default Dialog;
